/**
 * original source: https://ui.shadcn.com/docs/components/tooltip
 */
import * as React from 'react'
import { Tooltip as TooltipPrimitive } from 'radix-ui'

import type { RegisterableHotkey } from '@tanstack/react-hotkeys'
import { tv } from '@joyful/utils'
import type { VariantProps } from '@joyful/utils'
import { Slot } from './Slot'
import { Keyboard } from './Keyboard'
import type { ComponentProps, FocusEvent } from 'react'

export type TooltipProviderProps = ComponentProps<
  typeof TooltipPrimitive.Provider
>

export function TooltipProvider(props: TooltipProviderProps) {
  return (
    <TooltipPrimitive.Provider
      delayDuration={600}
      disableHoverableContent
      {...props}
    />
  )
}

export type TooltipRootProps = ComponentProps<typeof TooltipPrimitive.Root>

export type TooltipTriggerProps = React.ComponentProps<
  typeof TooltipPrimitive.Trigger
>

export function TooltipTrigger({ onFocus, ...props }: TooltipTriggerProps) {
  return (
    <TooltipPrimitive.Trigger
      onFocus={(event) => {
        if (!_isFocusVisibleTooltipActivation(event)) {
          event.preventDefault()
        }

        onFocus?.(event)
      }}
      {...props}
    />
  )
}

function _isFocusVisibleTooltipActivation(event: FocusEvent<HTMLElement>) {
  if (event.currentTarget.matches(':focus-visible')) {
    return true
  }

  return (
    event.target instanceof Element && event.target.matches(':focus-visible')
  )
}

export const tooltipContentVariants = tv({
  base: 'border-tremor-border text-tremor-content-emphasis bg-tremor-background animate-in fade-in-0 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 z-40 origin-(--radix-tooltip-content-transform-origin) overflow-hidden rounded-md border px-3 py-1.5 text-xs whitespace-normal duration-100 wrap-break-word',

  defaultVariants: {
    size: 'xs',
  },

  variants: {
    closeAnimation: {
      instant: 'data-[state=closed]:duration-0',
    },
    padding: {
      compact: 'p-3',
      none: 'p-0',
    },
    size: {
      auto: 'max-w-none',
      lg: 'max-w-96',
      md: 'max-w-md',
      sm: 'max-w-sm',
      xs: 'max-w-xs',
    },
    wrap: {
      pre: 'whitespace-pre-wrap',
    },
  },
})

export type TooltipContentProps = Omit<
  React.ComponentProps<typeof TooltipPrimitive.Content>,
  'className'
> &
  VariantProps<typeof tooltipContentVariants>

function TooltipContent({
  closeAnimation,
  padding,
  ref,
  sideOffset = 4,
  size,
  wrap,
  ...props
}: TooltipContentProps) {
  return (
    <TooltipPrimitive.Portal>
      <TooltipPrimitive.Content
        ref={ref}
        sideOffset={sideOffset}
        className={tooltipContentVariants({
          closeAnimation,
          padding,
          size,
          wrap,
        })}
        {...props}
      />
    </TooltipPrimitive.Portal>
  )
}

export type TooltipContentDescriptionProps = Omit<
  React.ComponentProps<'p'>,
  'className'
>

export function TooltipContentDescription({
  ref,
  ...props
}: TooltipContentDescriptionProps) {
  return (
    <p
      ref={ref}
      className="font-sans text-sm font-normal text-pretty"
      {...props}
    />
  )
}

export interface TooltipProps extends Omit<
  TooltipTriggerProps,
  'children' | 'content'
> {
  readonly children: React.ReactElement<unknown>

  readonly content: React.ReactNode
  readonly contentProps?: Omit<TooltipContentProps, 'children'>
  /**
   * @default false
   */
  readonly disabled?: boolean
  readonly hotkey?: RegisterableHotkey
  /**
   * @default false
   */
  readonly isPortaled?: boolean
  readonly subtitle?: string
  readonly title?: string
  readonly tooltipRootProps?: TooltipRootProps
}

export function Tooltip({
  children,
  content,
  contentProps,
  disabled = false,
  hotkey,
  isPortaled = false,
  ref,
  subtitle,
  title,
  tooltipRootProps,
  ...tooltipTriggerProps
}: TooltipProps) {
  if (disabled) {
    return <Slot {...tooltipTriggerProps}>{children}</Slot>
  }

  const _content = (
    <TooltipContent {...contentProps}>
      {typeof title === 'string' ? (
        <p className="mb-1 font-sans text-sm font-semibold">{title}</p>
      ) : null}

      {typeof content === 'string' ? (
        <TooltipContentDescription>
          {content}
          {hotkey !== undefined ? (
            <span className="ml-1.5">
              <Keyboard>{hotkey}</Keyboard>
            </span>
          ) : null}
        </TooltipContentDescription>
      ) : (
        content
      )}

      {typeof subtitle === 'string' ? (
        <p className="text-tremor-content font-sans text-xs font-normal text-balance">
          {subtitle}
        </p>
      ) : (
        subtitle
      )}
    </TooltipContent>
  )

  return (
    <TooltipPrimitive.Root {...tooltipRootProps}>
      <TooltipTrigger ref={ref} asChild {...tooltipTriggerProps}>
        {children}
      </TooltipTrigger>
      {isPortaled ? (
        <TooltipPrimitive.Portal>{_content}</TooltipPrimitive.Portal>
      ) : (
        _content
      )}
    </TooltipPrimitive.Root>
  )
}
